import React, { useState } from 'react';
import './Login.css';

const LOGO = 'https://fotileams.com/fotile-logo-red.png';
const DEVICE_KEY = 'device_id';

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    // crypto.randomUUID() is supported in all modern browsers
    id = (crypto.randomUUID && crypto.randomUUID()) ||
         'dev-' + Date.now() + '-' + Math.random().toString(36).substring(2);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export default function Login({ onLogin }) {
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword]     = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  // Device confirmation modal state
  const [showDeviceConfirm, setShowDeviceConfirm] = useState(false);
  const [pendingLoginData, setPendingLoginData]    = useState(null);
  const [registering, setRegistering]               = useState(false);

  const handleLogin = async () => {
    if (!employeeId || !password) {
      setError('Please enter your Employee ID and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const deviceId = getOrCreateDeviceId();

      const res = await fetch('https://fotileams.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: employeeId, password, device_id: deviceId })
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.device_locked) {
          setError('🔒 ' + data.error);
        } else {
          setError(data.error || 'Login failed');
        }
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('employee', JSON.stringify(data.employee));

      if (data.employee?.requires_device_confirm) {
        // First-time login — ask to confirm this is their primary device
        setPendingLoginData(data.employee);
        setShowDeviceConfirm(true);
      } else {
        onLogin(data.employee, data.token);
      }
    } catch (err) {
      setError('Cannot connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDevice = async (confirm) => {
    setRegistering(true);
    try {
      if (confirm) {
        const deviceId = getOrCreateDeviceId();
        await fetch('https://fotileams.com/api/auth/register-device', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ device_id: deviceId }),
        });
      }
      setShowDeviceConfirm(false);
      onLogin(pendingLoginData, localStorage.getItem('token'));
    } catch {
      // Even if registration fails, let them continue — they'll be asked again next login
      setShowDeviceConfirm(false);
      onLogin(pendingLoginData, localStorage.getItem('token'));
    } finally {
      setRegistering(false);
    }
  };

  if (showDeviceConfirm) {
    return (
      <div className="login-page">
        <div className="login-box">
          <div className="login-logo">
            <img src={LOGO} alt="Fotile" className="login-logo-img" />
          </div>
          <div className="dc-icon">📱</div>
          <div className="dc-title">Register This Device?</div>
          <p className="dc-sub">
            For security, your account will be locked to this device.
            Use this same phone/browser every time you clock in.
          </p>
          <div className="dc-buttons">
            <button className="dc-btn dc-btn-secondary" onClick={() => confirmDevice(false)} disabled={registering}>
              Not Now
            </button>
            <button className="dc-btn dc-btn-primary" onClick={() => confirmDevice(true)} disabled={registering}>
              {registering ? 'Saving…' : 'Yes, This Is My Device'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          <img src={LOGO} alt="Fotile" className="login-logo-img" />
          <div className="login-brand">Fotile AMS</div>
          <div className="login-sub">Employee Attendance Portal</div>
        </div>

        {error && <div className="login-error">{error}</div>}

        <div className="login-form">
          <div className="login-group">
            <label className="login-label">Employee ID</label>
            <input
              className="login-input"
              type="text"
              placeholder="e.g. EMP-001"
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>
          <div className="login-group">
            <label className="login-label">Password</label>
            <input
              className="login-input"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>
          <button
            className={`login-btn ${loading ? 'loading' : ''}`}
            onClick={handleLogin}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>

        <div className="login-footer">
          Forgot your password? Contact HR
        </div>
      </div>
    </div>
  );
}